/**
 * The real transport for the MAHI conversation flow — POST + manual SSE
 * line parsing against /api/mira/agent/chat (backend/routes/mira/agent.py).
 * A raw fetch() is required rather than EventSource because EventSource is
 * GET-only and can't carry a custom Authorization header; the endpoint
 * returns `text/event-stream` lines shaped `data: {...}\n\n`, with event
 * types `text` (a prose delta), `products` (ground-truth results, surfaced
 * as soon as the tool call returns), `error`, and `done` (the turn's final
 * text/products/conversation_id/stage).
 *
 * Same `{ sendTurn({message, onEvent}) => cancel }` shape as
 * mahiMockTransport's createMockTransport() — that's what let stop point
 * (a), the orb→panel→chip transform, be built and reviewed against a
 * scripted conversation before this file existed. Swapping which transport
 * DiscoveryFeed constructs is the entire integration; useMahiConversation
 * and MahiChatPanel are unchanged.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const CHAT_URL = `${API_BASE}/api/mira/agent/chat`;
const DEGRADED_MESSAGE = "Let me think on that - try me again in a sec.";
// Reset on every frame received, not just once at the start — a slow but
// still-streaming turn (a genuinely large brand catalog) should never be
// killed for taking a while; true dead air is what this is for.
const WATCHDOG_MS = 45_000;

export function createLiveTransport() {
  // Threaded through so the agent keeps the thread across turns; cleared by
  // `reset()` when the results chip is dismissed, so refining an old search
  // and starting a brand new one never share server-side context.
  let conversationId = null;

  function sendTurn({ message, onEvent }) {
    const controller = new AbortController();
    let settled = false;
    let watchdog = null;

    const stopWatchdog = () => clearTimeout(watchdog);
    const bumpWatchdog = () => {
      stopWatchdog();
      watchdog = setTimeout(() => {
        if (settled) return;
        settled = true;
        controller.abort();
        onEvent({ type: 'error', message: DEGRADED_MESSAGE });
      }, WATCHDOG_MS);
    };
    const emit = (payload) => {
      if (settled) return;
      if (payload.type === 'done' || payload.type === 'error') {
        settled = true;
        stopWatchdog();
      } else {
        bumpWatchdog();
      }
      onEvent(payload);
    };

    bumpWatchdog();

    (async () => {
      let response;
      try {
        const token = localStorage.getItem('mira_token');
        response = await fetch(CHAT_URL, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ message, conversation_id: conversationId }),
        });
      } catch {
        if (!controller.signal.aborted) emit({ type: 'error', message: DEGRADED_MESSAGE });
        return;
      }

      if (!response.ok || !response.body) {
        emit({ type: 'error', message: DEGRADED_MESSAGE });
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are blank-line-separated; this endpoint only ever
          // emits a single `data:` line per frame, but splitting on the
          // frame boundary first (rather than just any newline) keeps this
          // correct if that ever changes.
          let sepIndex = buffer.indexOf('\n\n');
          while (sepIndex !== -1) {
            const frame = buffer.slice(0, sepIndex);
            buffer = buffer.slice(sepIndex + 2);
            const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
            sepIndex = buffer.indexOf('\n\n');
            if (!dataLine) continue;

            let payload;
            try {
              payload = JSON.parse(dataLine.slice(5).trim());
            } catch {
              continue;
            }
            if (payload.type === 'done' && payload.conversation_id) {
              conversationId = payload.conversation_id;
            }
            emit(payload);
          }
        }
      } catch {
        if (!controller.signal.aborted) emit({ type: 'error', message: DEGRADED_MESSAGE });
      } finally {
        stopWatchdog();
      }
    })();

    return () => {
      stopWatchdog();
      controller.abort();
    };
  }

  function reset() {
    conversationId = null;
  }

  // Chat history's "pick this back up" — the next sendTurn() continues this
  // exact server-side thread instead of minting a new one under messages
  // that were only just restored client-side.
  function seedConversationId(id) {
    conversationId = id;
  }

  return { sendTurn, reset, seedConversationId };
}
